import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Smartphone, 
  Monitor,
  Tablet,
  Calendar,
  Clock,
  Loader2,
  QrCode
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const StoreAnalyticsDialog = ({ store, token, open, onOpenChange }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && store) {
      fetchAnalytics();
    }
  }, [open, store]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/stores/${store.id}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      case 'desktop': return <Monitor className="w-4 h-4" />;
      default: return <QrCode className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#5DBDB3]" />
            Store Analytics
          </DialogTitle>
          <DialogDescription>
            {store?.name} - QR Code scan statistics
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : analytics ? (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <QrCode className="w-5 h-5 text-[#5DBDB3] mx-auto mb-1" />
                  <p className="text-2xl font-bold">{analytics.total_scans}</p>
                  <p className="text-xs text-muted-foreground">Total Scans</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Users className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold">{analytics.unique_users}</p>
                  <p className="text-xs text-muted-foreground">Unique Users</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Calendar className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold">{analytics.scans_today}</p>
                  <p className="text-xs text-muted-foreground">Today</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-5 h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold">{analytics.scans_this_week}</p>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </CardContent>
              </Card>
            </div>

            {/* Device Breakdown */}
            {Object.keys(analytics.device_breakdown || {}).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Device Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(analytics.device_breakdown).map(([device, count]) => (
                      <div 
                        key={device} 
                        className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg"
                      >
                        {getDeviceIcon(device)}
                        <span className="text-sm capitalize">{device}</span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Daily Scans Chart */}
            {analytics.daily_scans?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Last 30 Days</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-end gap-1 h-32">
                    {analytics.daily_scans.map((day, index) => {
                      const maxScans = Math.max(...analytics.daily_scans.map(d => d.scans));
                      const height = maxScans > 0 ? (day.scans / maxScans) * 100 : 0;
                      return (
                        <div
                          key={day.date}
                          className="flex-1 bg-[#5DBDB3] rounded-t hover:bg-[#4AA89E] transition-colors cursor-pointer group relative"
                          style={{ height: `${Math.max(height, 4)}%` }}
                          title={`${day.date}: ${day.scans} scans`}
                        >
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground text-background px-2 py-1 rounded text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                            {day.scans} scans
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                    <span>{analytics.daily_scans[0]?.date}</span>
                    <span>{analytics.daily_scans[analytics.daily_scans.length - 1]?.date}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent Scans */}
            {analytics.recent_scans?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Recent Scans</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {analytics.recent_scans.slice(0, 10).map((scan, index) => (
                      <div 
                        key={scan.id || index} 
                        className="flex items-center justify-between py-2 border-b last:border-0"
                      >
                        <div className="flex items-center gap-2">
                          {getDeviceIcon(scan.device_type)}
                          <span className="text-sm capitalize">{scan.device_type || 'Unknown'}</span>
                          {scan.user_id && (
                            <Badge variant="outline" className="text-xs">Logged in</Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDate(scan.scanned_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Empty State */}
            {analytics.total_scans === 0 && (
              <Card>
                <CardContent className="p-8 text-center">
                  <QrCode className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <h3 className="font-medium mb-1">No Scans Yet</h3>
                  <p className="text-sm text-muted-foreground">
                    Share your store QR code with customers to start tracking scans.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default StoreAnalyticsDialog;
